// src/features/auth/components/AppRecommendations.tsx
import React from 'react';
import { DevicePhoneMobileIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

interface AppRecommendationsProps {
  onContinue: () => void;
  loading?: boolean;
}

interface AuthenticatorApp {
  name: string;
  description: string;
  platforms: string[];
  downloadLinks: {
    ios?: string;
    android?: string;
    web?: string;
  };
  icon: string;
}

const authenticatorApps: AuthenticatorApp[] = [
  {
    name: 'Google Authenticator',
    description: 'Free, simple, and secure 2FA app by Google',
    platforms: ['iOS', 'Android'],
    downloadLinks: {
      ios: 'https://apps.apple.com/app/google-authenticator/id388497605',
      android: 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'
    },
    icon: '🔐'
  },
  {
    name: 'Microsoft Authenticator',
    description: 'Secure 2FA with backup and sync features',
    platforms: ['iOS', 'Android'],
    downloadLinks: {
      ios: 'https://apps.apple.com/app/microsoft-authenticator/id983156458',
      android: 'https://play.google.com/store/apps/details?id=com.azure.authenticator'
    },
    icon: '🛡️'
  },
  {
    name: 'Authy',
    description: 'Multi-device 2FA with cloud backup',
    platforms: ['iOS', 'Android', 'Desktop'],
    downloadLinks: {
      ios: 'https://apps.apple.com/app/authy/id494168017',
      android: 'https://play.google.com/store/apps/details?id=com.authy.authy',
      web: 'https://authy.com/download/'
    },
    icon: '🔑'
  },
  {
    name: '1Password',
    description: 'Password manager with built-in 2FA',
    platforms: ['iOS', 'Android', 'Desktop'],
    downloadLinks: {
      ios: 'https://apps.apple.com/app/1password-7-password-manager/id1333542190',
      android: 'https://play.google.com/store/apps/details?id=com.onepassword.android',
      web: 'https://1password.com/downloads/'
    },
    icon: '🔒'
  }
];

const AppRecommendations: React.FC<AppRecommendationsProps> = ({ onContinue, loading }) => {
  const handleAppDownload = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <DevicePhoneMobileIcon className="h-12 w-12 text-primary-light dark:text-primary-dark mx-auto mb-3" />
        <h4 className="text-lg font-medium text-theme-text dark:text-dark-theme-text mb-2">
          Choose Your Authenticator App
        </h4>
        <p className="text-sm text-theme-text-secondary dark:text-dark-theme-text-secondary">
          You'll need an authenticator app to generate verification codes. Choose one below or use any TOTP-compatible app.
        </p>
      </div>

      {/* App Grid */}
      <div className="grid gap-4 sm:grid-cols-2">
        {authenticatorApps.map((app) => (
          <div
            key={app.name}
            className="border border-theme-border dark:border-dark-theme-border rounded-lg p-4 transition-colors duration-150"
          >
            <div className="flex items-start space-x-3">
              <div className="text-2xl">{app.icon}</div>
              <div className="flex-1 min-w-0">
                <h5 className="text-sm font-medium text-theme-text dark:text-dark-theme-text mb-1">
                  {app.name}
                </h5>
                <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary mb-2">
                  {app.description}
                </p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {app.platforms.map((platform) => (
                    <span
                      key={platform}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary text-theme-text-secondary dark:text-dark-theme-text-secondary"
                    >
                      {platform}
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  {app.downloadLinks.ios && (
                    <button
                      onClick={() => handleAppDownload(app.downloadLinks.ios!)}
                      className="inline-flex items-center text-xs text-primary-light dark:text-primary-dark hover:underline"
                    >
                      iOS <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                    </button>
                  )}
                  {app.downloadLinks.android && (
                    <button
                      onClick={() => handleAppDownload(app.downloadLinks.android!)}
                      className="inline-flex items-center text-xs text-primary-light dark:text-primary-dark hover:underline"
                    >
                      Android <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                    </button>
                  )}
                  {app.downloadLinks.web && (
                    <button
                      onClick={() => handleAppDownload(app.downloadLinks.web!)}
                      className="inline-flex items-center text-xs text-primary-light dark:text-primary-dark hover:underline"
                    >
                      Desktop <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alternative Options */}
      <div className="bg-theme-bg-secondary dark:bg-dark-theme-bg-secondary rounded-md p-4">
        <h5 className="text-sm font-medium text-theme-text dark:text-dark-theme-text mb-2">
          Already have an authenticator app?
        </h5>
        <p className="text-xs text-theme-text-secondary dark:text-dark-theme-text-secondary">
          Any TOTP-compatible authenticator app will work, including Bitwarden, LastPass Authenticator, 
          or other password managers with 2FA support.
        </p>
      </div>

      {/* Continue Button */}
      <div className="flex justify-center pt-4">
        <button
          onClick={onContinue}
          disabled={loading}
          className="px-6 py-2 bg-gradient-button text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-light dark:focus:ring-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-opacity duration-150"
        >
          {loading ? 'Setting up...' : 'I have an authentication app'}
        </button>
      </div>
    </div>
  );
};

export default AppRecommendations;
